import { useEffect, useRef, useState } from "react";

import { useSubscription } from "@apollo/client/react";
import { differenceInMinutes } from "date-fns";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import semver from "semver";

import { RS_API_URI } from "@api/session";

import { VersionsDocument } from "@/gql/graphql";
import { useAppDispatch, useAppSelector } from "@/hooks";

import { bottomBarSelector, setUpdateAvailable } from "../bottomBarReducer";
import ReloadModal from "./ReloadModal";

const getCurrentVersionHandler = async () => {
  try {
    const rsp = await fetch(`${RS_API_URI}/versions`);
    const versions: { frontend: string; backend: string } = await rsp.json();
    const event = new CustomEvent("tt:versions", { detail: versions });
    document.dispatchEvent(event);
  } catch (e) {
    console.log("Failed to fetch current version", e);
  }
};

export default function Version() {
  const [updatedAt, setUpdatedAt] = useState<Nullable<Date>>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const tm = useRef<number>(undefined);
  const { t } = useTranslation(["bottomBar"]);
  const refreshPage = () => {
    window.location.reload();
  };
  const { updateAvailable } = useAppSelector(bottomBarSelector);
  const dispatch = useAppDispatch();

  useSubscription(VersionsDocument, {
    onData: ({ data: { data } }) => {
      if (!data?.versions) return;

      if (!import.meta.env.VITE_VERSION || semver.lte(data.versions.frontend, import.meta.env.VITE_VERSION)) {
        return;
      }

      setUpdatedAt(new Date());
      dispatch(setUpdateAvailable(true));
    },
  });

  useEffect(() => {
    if (tm.current) {
      window.clearInterval(tm.current);
      tm.current = undefined;
    }

    if (updatedAt) {
      tm.current = window.setInterval(() => {
        const diff = differenceInMinutes(new Date(), updatedAt);

        if (diff === 20) {
          localStorage.setItem("automatic_reload_after_version_update", "1");
          window.location.reload();
          return;
        }

        if (diff === 5) {
          setModalVisible(true);
        }
      }, 60000);
    }

    return () => {
      if (tm.current) window.clearInterval(tm.current);
      tm.current = undefined;
    };
  }, [updatedAt]);

  useEffect(() => {
    getCurrentVersionHandler();
  }, []);

  useEffect(() => {
    window.addEventListener("focus", getCurrentVersionHandler);

    return () => {
      window.removeEventListener("focus", getCurrentVersionHandler);
    };
  });

  if (!updateAvailable) {
    return null;
  }

  return (
    <div>
      {t("bottomBar:update_available")}
      <Button onClick={refreshPage}>{t("bottomBar:reload")}</Button>

      <ReloadModal show={modalVisible} onHide={() => setModalVisible(false)} />
    </div>
  );
}
